import { modifierBuilder } from '@carboncopyco/editor-utils';
import { Button, InputText } from '@carboncopyco/ui';
import { isEqual } from 'lodash-es';
import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useTextLengthValidation } from '../../hooks';
import { ErrorMessageText, FormField } from '../FormField';
import type { DynamicResponseModifier } from './CreateDynamicResponse';

type DynamicResponseModifierFormProps = {
  onSubmit: (modifier: DynamicResponseModifier) => void;
  onCancel?: () => void;
  modifier?: DynamicResponseModifier;
};

const MAX_CHARACTERS = 1000;

export const DynamicResponseModifierForm = ({
  onSubmit,
  onCancel,
  modifier,
}: DynamicResponseModifierFormProps) => {
  const [findValue, setFindValue] = useState(modifier?.arguments.match ?? '');
  const [replaceWithValue, setRepalceWithValue] = useState(
    modifier?.arguments.replace ?? ''
  );

  const [findError, setFindError] = useState<string>(null);
  const [replaceWithError, setReplaceWithError] = useState<string>(null);

  const { validateText } = useTextLengthValidation({
    maxCharacters: MAX_CHARACTERS,
  });

  const isFormChanged = useCallback(() => {
    return !isEqual(
      {
        match: modifier?.arguments.match,
        replace: modifier?.arguments.replace,
      },
      {
        match: findValue,
        replace: replaceWithValue,
      }
    );
  }, [modifier, findValue, replaceWithValue]);

  const isSubmitButtonDisabled =
    !findValue ||
    !replaceWithValue ||
    !!findError ||
    !!replaceWithError ||
    (modifier && !isFormChanged());

  const onSubmitHandler = useCallback(() => {
    if (modifier) {
      onSubmit({
        ...modifier,
        arguments: {
          match: findValue,
          replace: replaceWithValue,
        },
      });

      return;
    }

    onSubmit({
      id: uuid(),
      ...modifierBuilder.dynamic({
        match: findValue,
        replace: replaceWithValue,
      }),
    });
  }, [modifier, findValue, replaceWithValue, onSubmit]);

  const onChangeHandler = useCallback(
    async (field: 'find' | 'replace', value: string) => {
      const hasError = await validateText(value);

      if (hasError) {
        return field === 'find'
          ? setFindError(hasError)
          : setReplaceWithError(hasError);
      }

      if (field === 'find') {
        setFindValue(value);
        setFindError(null);
      } else {
        setRepalceWithValue(value);
        setReplaceWithError(null);
      }
    },
    [setFindValue]
  );

  return (
    <div className="flex flex-col gap-2.5">
      <FormField
        name="find"
        label="When finding"
      >
        <InputText
          autoFocus
          name="find"
          size="md"
          multiline
          multilineOptions={{
            height: 'md',
            isExpandableHeight: true,
          }}
          value={findValue}
          onChange={(e) => onChangeHandler('find', e.target.value)}
          state={findError ? 'error' : 'normal'}
        />
        {findError && <ErrorMessageText message={findError} />}
      </FormField>
      <FormField
        name="replace"
        label="Replace it with"
      >
        <InputText
          name="replace"
          size="md"
          multiline
          multilineOptions={{
            height: 'md',
            isExpandableHeight: true,
          }}
          value={replaceWithValue}
          onChange={(e) => onChangeHandler('replace', e.target.value)}
          state={replaceWithError ? 'error' : 'normal'}
        />
        {replaceWithError && <ErrorMessageText message={replaceWithError} />}
      </FormField>
      <div className="flex gap-2.5 justify-end pt-1.5">
        <Button
          label="Cancel"
          onClick={onCancel}
          highContrast
        />
        <Button
          label={modifier ? 'Save' : 'Add'}
          onClick={onSubmitHandler}
          disabled={isSubmitButtonDisabled}
        />
      </div>
    </div>
  );
};
